import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { getStoreProducts } from '@/api/productApi';
import type { StoreProduct } from '@/api/productApi';
import {
  createSale,
  getSaleProductItems,
  toSaleApiError,
} from '@/api/saleApi';
import type {
  CreatedSale,
  CreateSaleRequest,
  SaleProductItem,
} from '@/api/saleApi';
import { formatCurrency } from '@/utils/helpers';
import PosCartItem from '../components/PosCartItem';
import PosProductCard from '../components/PosProductCard';
import ProductItemSelectorModal from '../components/ProductItemSelectorModal';
import type { CartLine, SaleProduct, TrackingMode } from '../posTypes';

interface ProductItemSelection {
  product: SaleProduct;
  items: SaleProductItem[];
  selectedItemIds: number[];
}

interface SaleReceipt {
  sale: CreatedSale;
  paidAmount: number;
}

const inputClass = 'h-10 w-full rounded-xl border border-outline-variant bg-white px-3 font-body-sm text-body-sm text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:bg-surface-container-low disabled:text-secondary';

const SALE_ERROR_TRANSLATION_KEYS: Record<string, string> = {
  '2002': 'admin.sales.errors.product_missing',
  '2003': 'admin.sales.errors.inventory_unavailable',
  '2014': 'admin.sales.errors.item_missing',
  '2015': 'admin.sales.errors.customer_missing',
  '3001': 'admin.sales.errors.insufficient_inventory',
  '3002': 'admin.sales.errors.item_variant_mismatch',
  '3003': 'admin.sales.errors.item_unavailable',
  '3004': 'admin.sales.errors.quantity_mismatch',
  '3005': 'admin.sales.errors.duplicate_item',
  '3006': 'admin.sales.errors.duplicate_variant',
  '3007': 'admin.sales.errors.inventory_mismatch',
  '3008': 'admin.sales.errors.amount_insufficient',
};

const getProductIcon = (categoryName = ''): string =>
  categoryName.toLocaleLowerCase('vi-VN').includes('tai nghe') ? 'headphones' : 'smartphone';

const mapStoreProducts = (data: StoreProduct[]): SaleProduct[] =>
  data.flatMap((product) => (product.productVariants ?? []).map((variant) => ({
    id: variant.id,
    productId: variant.productId,
    name: product.name,
    versionName: variant.versionName,
    color: variant.color,
    variant: `${variant.versionName} - ${variant.color}`,
    price: variant.price,
    stock: variant.inventory?.quantity ?? 0,
    icon: getProductIcon(product.category?.name),
  })));

const AdminSalesPage: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState<SaleProduct[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [trackingModes, setTrackingModes] = useState<Record<number, TrackingMode>>({});
  const [loadingVariantId, setLoadingVariantId] = useState<number | null>(null);
  const [productItemSelection, setProductItemSelection] = useState<ProductItemSelection | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [note, setNote] = useState('');
  const [paidAmountInput, setPaidAmountInput] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [receipt, setReceipt] = useState<SaleReceipt | null>(null);
  const productRequestId = useRef(0);
  const productItemRequestId = useRef(0);
  const submitLock = useRef(false);
  const cartRef = useRef<CartLine[]>([]);

  const loadProducts = useCallback(async () => {
    const requestId = ++productRequestId.current;
    setIsLoadingProducts(true);
    setProductsError('');
    try {
      const response = await getStoreProducts();
      if (requestId !== productRequestId.current) return;

      const nextProducts = mapStoreProducts(response.data);
      setProducts(nextProducts);
      setCart((currentCart) => currentCart.map((line) => {
        const latestProduct = nextProducts.find((product) => product.id === line.id);
        return latestProduct
          ? { ...line, price: latestProduct.price, stock: latestProduct.stock }
          : line;
      }));
    } catch {
      if (requestId === productRequestId.current) {
        setProductsError('admin.sales.products.load_error');
      }
    } finally {
      if (requestId === productRequestId.current) setIsLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    void loadProducts();
    return () => {
      productRequestId.current += 1;
      productItemRequestId.current += 1;
    };
  }, [loadProducts]);

  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase('vi-VN');
    if (!normalizedSearch) return products;
    return products.filter((product) =>
      `${product.name} ${product.versionName} ${product.color}`
        .toLocaleLowerCase('vi-VN')
        .includes(normalizedSearch));
  }, [products, search]);

  const subtotal = cart.reduce((total, line) => total + line.price * line.quantity, 0);
  const totalItems = cart.reduce((total, line) => total + line.quantity, 0);
  const paidAmount = Number(paidAmountInput);
  const validPaidAmount = Number.isFinite(paidAmount) ? paidAmount : 0;
  const changeAmount = Math.max(validPaidAmount - subtotal, 0);

  const getSaleErrorMessage = (error: unknown): string => {
    const normalizedError = toSaleApiError(error);
    const errorCode = normalizedError.code === undefined ? '' : String(normalizedError.code);
    return t(SALE_ERROR_TRANSLATION_KEYS[errorCode] ?? 'admin.sales.errors.sale_failed');
  };

  const resetSale = () => {
    setCart([]);
    setCustomerName('');
    setCustomerPhone('');
    setCustomerAddress('');
    setNote('');
    setPaidAmountInput('');
    setSubmitError('');
    setReceipt(null);
    setProductItemSelection(null);
  };

  const addQuantityProduct = (product: SaleProduct) => {
    setCart((currentCart) => {
      const existingLine = currentCart.find((line) => line.id === product.id);
      if (existingLine) {
        return currentCart.map((line) => line.id === product.id
          ? { ...line, quantity: Math.min(line.quantity + 1, product.stock) }
          : line);
      }
      return [...currentCart, {
        ...product,
        quantity: 1,
        trackingMode: 'QUANTITY',
        selectedProductItems: [],
      }];
    });
  };

  const openProductItemSelector = (product: SaleProduct, items: SaleProductItem[]) => {
    const existingLine = cartRef.current.find((line) => line.id === product.id);
    const availableIds = new Set(items.map((item) => item.id));
    setProductItemSelection({
      product,
      items,
      selectedItemIds: existingLine?.selectedProductItems
        .map((item) => item.id)
        .filter((id) => availableIds.has(id)) ?? [],
    });
  };

  const handleProductClick = async (product: SaleProduct) => {
    if (product.stock <= 0 || loadingVariantId === product.id) return;

    setReceipt(null);
    setSubmitError('');
    const requestId = ++productItemRequestId.current;
    if (trackingModes[product.id] === 'QUANTITY') {
      setLoadingVariantId(null);
      addQuantityProduct(product);
      return;
    }

    setLoadingVariantId(product.id);
    try {
      const items = await getSaleProductItems(product.id);
      if (requestId !== productItemRequestId.current) return;

      if (items.length === 0) {
        setTrackingModes((current) => ({ ...current, [product.id]: 'QUANTITY' }));
        addQuantityProduct(product);
        return;
      }

      setTrackingModes((current) => ({ ...current, [product.id]: 'PRODUCT_ITEM' }));
      openProductItemSelector(product, items);
    } catch (error: unknown) {
      if (requestId === productItemRequestId.current) {
        message.error(getSaleErrorMessage(error));
      }
    } finally {
      if (requestId === productItemRequestId.current) setLoadingVariantId(null);
    }
  };

  const confirmProductItems = (selectedItems: SaleProductItem[]) => {
    if (!productItemSelection) return;
    const { product } = productItemSelection;
    const selectedIds = new Set(selectedItems.map((item) => item.id));
    if (selectedIds.size !== selectedItems.length) {
      setSubmitError(t('admin.sales.validation.duplicate_item'));
      return;
    }

    setReceipt(null);
    setSubmitError('');
    setCart((currentCart) => {
      const nextLine: CartLine = {
        ...product,
        quantity: selectedItems.length,
        trackingMode: 'PRODUCT_ITEM',
        selectedProductItems: selectedItems,
      };
      const hasExistingLine = currentCart.some((line) => line.id === product.id);
      return hasExistingLine
        ? currentCart.map((line) => line.id === product.id ? nextLine : line)
        : [...currentCart, nextLine];
    });
    setProductItemSelection(null);
  };

  const updateQuantity = (id: number, change: number) => {
    setReceipt(null);
    setSubmitError('');
    setCart((currentCart) => currentCart.flatMap((line) => {
      if (line.id !== id || line.trackingMode !== 'QUANTITY') return [line];
      const nextQuantity = line.quantity + change;
      if (nextQuantity <= 0) return [];
      return [{ ...line, quantity: Math.min(nextQuantity, line.stock) }];
    }));
  };

  const removeLine = (id: number) => {
    setReceipt(null);
    setSubmitError('');
    setCart((currentCart) => currentCart.filter((line) => line.id !== id));
  };

  const validateSale = (): string => {
    if (cart.length === 0) return t('admin.sales.validation.product_required');
    if (customerName.trim().length > 200) return t('admin.sales.validation.customer_name_max');
    if (customerPhone.trim().length > 30) return t('admin.sales.validation.customer_phone_max');
    if (customerAddress.trim().length > 500) return t('admin.sales.validation.customer_address_max');
    if (note.trim().length > 2000) return t('admin.sales.validation.note_max');

    const variantIds = new Set<number>();
    const productItemIds = new Set<number>();
    for (const line of cart) {
      if (variantIds.has(line.id)) return t('admin.sales.validation.duplicate_variant');
      variantIds.add(line.id);
      if (line.stock <= 0 || line.quantity > line.stock) {
        return t('admin.sales.validation.insufficient_stock', {
          product: `${line.name} - ${line.variant}`,
        });
      }
      if (line.quantity < 1) {
        return t('admin.sales.validation.quantity_positive', { product: line.name });
      }

      if (line.trackingMode === 'PRODUCT_ITEM') {
        if (line.selectedProductItems.length !== line.quantity) {
          return t('admin.sales.validation.reselect_items', {
            product: `${line.name} - ${line.variant}`,
          });
        }
        for (const item of line.selectedProductItems) {
          if (productItemIds.has(item.id)) return t('admin.sales.validation.duplicate_item');
          productItemIds.add(item.id);
        }
      }
    }

    if (!Number.isFinite(paidAmount) || paidAmount <= 0) {
      return t('admin.sales.validation.amount_required');
    }
    if (paidAmount < subtotal) return t('admin.sales.validation.amount_insufficient');
    return '';
  };

  const refreshUnavailableProductItems = async () => {
    const trackedLines = cart.filter((line) => line.trackingMode === 'PRODUCT_ITEM');
    for (const line of trackedLines) {
      const requestId = ++productItemRequestId.current;
      setLoadingVariantId(line.id);
      try {
        const items = await getSaleProductItems(line.id);
        if (requestId !== productItemRequestId.current) return;
        const availableIds = new Set(items.map((item) => item.id));
        const validSelectedIds = line.selectedProductItems
          .map((item) => item.id)
          .filter((id) => availableIds.has(id));
        if (validSelectedIds.length !== line.selectedProductItems.length) {
          setProductItemSelection({
            product: line,
            items,
            selectedItemIds: validSelectedIds,
          });
          return;
        }
      } catch (error: unknown) {
        message.error(getSaleErrorMessage(error));
        return;
      } finally {
        if (requestId === productItemRequestId.current) setLoadingVariantId(null);
      }
    }
  };

  const handleSubmitSale = async () => {
    if (submitLock.current) return;
    const validationError = validateSale();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    const normalizedName = customerName.trim();
    const normalizedPhone = customerPhone.trim();
    const normalizedAddress = customerAddress.trim();
    const normalizedNote = note.trim();
    const input: CreateSaleRequest = {
      ...(normalizedName ? { customerName: normalizedName } : {}),
      ...(normalizedPhone ? { customerPhone: normalizedPhone } : {}),
      ...(normalizedAddress ? { customerAddress: normalizedAddress } : {}),
      items: cart.map((line) => ({
        productVariantId: line.id,
        quantity: line.quantity,
        ...(line.trackingMode === 'PRODUCT_ITEM'
          ? { productItemIds: line.selectedProductItems.map((item) => item.id) }
          : {}),
      })),
      payment: {
        method: 'CASH',
        paidAmount,
      },
      ...(normalizedNote ? { note: normalizedNote } : {}),
    };

    submitLock.current = true;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const sale = await createSale(input);
      setReceipt({ sale, paidAmount });
      setCart([]);
      setCustomerName('');
      setCustomerPhone('');
      setCustomerAddress('');
      setNote('');
      setPaidAmountInput('');
      message.success(t('admin.sales.notices.created'));
      await loadProducts();
    } catch (error: unknown) {
      const normalizedError = toSaleApiError(error);
      const errorCode = normalizedError.code === undefined ? '' : String(normalizedError.code);
      const errorMessage = t(
        SALE_ERROR_TRANSLATION_KEYS[errorCode] ?? 'admin.sales.errors.sale_failed',
      );
      setSubmitError(errorMessage);
      message.error(errorMessage);
      if (errorCode === '3001') await loadProducts();
      if (errorCode === '3003') await refreshUnavailableProductItems();
    } finally {
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };

  const blockedProductItemIds = productItemSelection
    ? cart
      .filter((line) => line.id !== productItemSelection.product.id)
      .flatMap((line) => line.selectedProductItems.map((item) => item.id))
    : [];

  return (
    <div className="mx-auto flex min-h-full w-full min-w-0 max-w-[1600px] flex-col gap-md sm:gap-lg">
      <header className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 font-label-sm text-label-sm text-secondary">
            <span className="material-symbols-outlined text-[18px]">storefront</span>
            {t('admin.sales.eyebrow')}
          </div>
          <h1 className="font-h1 text-h1 text-on-surface">{t('admin.sales.title')}</h1>
          <p className="mt-1 text-body-md text-secondary">{t('admin.sales.subtitle')}</p>
        </div>
        <button
          type="button"
          className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-outline-variant bg-white px-4 font-label-md text-secondary shadow-sm transition-colors hover:bg-surface-container-low focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          onClick={resetSale}
          disabled={isSubmitting}
        >
          <span className="material-symbols-outlined text-[19px]">refresh</span>
          {t('admin.sales.new_sale')}
        </button>
      </header>

      {receipt && (
        <section className="rounded-[20px] border border-primary/25 bg-primary-fixed/35 px-4 py-3" role="status">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-2 text-primary">
                <span className="material-symbols-outlined">check_circle</span>
                <h2 className="font-h3 text-h3 font-bold">{t('admin.sales.receipt.success')}</h2>
              </div>
              <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-body-sm sm:grid-cols-2 xl:grid-cols-5">
                <div className="flex gap-2"><dt className="text-secondary">{t('admin.sales.receipt.code')}:</dt><dd className="font-mono font-bold text-on-surface">{receipt.sale.code}</dd></div>
                <div className="flex gap-2"><dt className="text-secondary">{t('admin.sales.receipt.status')}:</dt><dd className="font-bold text-primary">{t(`admin.sales_history.status.${receipt.sale.status.toLowerCase()}`, { defaultValue: receipt.sale.status })}</dd></div>
                <div className="flex gap-2"><dt className="text-secondary">{t('admin.sales.receipt.total')}:</dt><dd className="font-mono text-on-surface">{formatCurrency(receipt.sale.totalAmount)}</dd></div>
                <div className="flex gap-2"><dt className="text-secondary">{t('admin.sales.receipt.amount_received')}:</dt><dd className="font-mono text-on-surface">{formatCurrency(receipt.paidAmount)}</dd></div>
                <div className="flex gap-2"><dt className="text-secondary">{t('admin.sales.receipt.change')}:</dt><dd className="font-mono font-bold text-primary">{formatCurrency(Math.max(receipt.paidAmount - receipt.sale.totalAmount, 0))}</dd></div>
              </dl>
            </div>
            <button type="button" className="self-start text-secondary hover:text-primary" onClick={() => setReceipt(null)} aria-label={t('admin.sales.receipt.close')}>
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </section>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-md xl:h-[calc(100dvh-12rem)] xl:min-h-[680px] xl:grid-cols-[minmax(0,3fr)_minmax(360px,2fr)]">
        <section className="flex min-h-[650px] min-w-0 flex-col overflow-hidden rounded-[20px] border border-outline-variant/70 bg-white shadow-sm xl:min-h-0">
          <div className="flex shrink-0 items-center justify-between border-b border-outline-variant px-4 py-3.5 sm:px-5">
            <div>
              <h2 className="font-h2 text-h2 text-on-surface">{t('admin.sales.current_sale.title')}</h2>
              <p className="mt-0.5 text-body-sm text-secondary">
                <span>{t('admin.sales.current_sale.products_count', { count: totalItems })}</span>
                <span aria-hidden="true"> · </span>
                <span>{t('admin.sales.current_sale.lines_count', { count: cart.length })}</span>
              </p>
            </div>
            <span className="inline-flex min-h-8 items-center rounded-full bg-primary-fixed px-3 font-label-sm text-primary">{t('admin.sales.current_sale.badge', { count: cart.length })}</span>
          </div>

          <div className="min-h-0 flex-1 overscroll-contain overflow-y-auto px-4 sm:px-5" role="list" aria-label={t('admin.sales.current_sale.list_label')}>
            {cart.length === 0 ? (
              <div className="flex h-full min-h-36 flex-col items-center justify-center py-8 text-center text-secondary">
                <span className="mb-2 rounded-full bg-surface-container-low p-2 material-symbols-outlined text-[32px]">shopping_cart</span>
                <p className="font-label-md text-on-surface">{t('admin.sales.current_sale.empty_title')}</p>
                <p className="mt-1 text-body-sm">{t('admin.sales.current_sale.empty_description')}</p>
              </div>
            ) : cart.map((line) => (
              <PosCartItem
                key={line.id}
                line={line}
                onQuantityChange={updateQuantity}
                onRemove={removeLine}
                onReselectProductItems={(selectedLine) => void handleProductClick(selectedLine)}
              />
            ))}
          </div>

          <div className="shrink-0 border-t border-outline-variant bg-white">
            <section className="border-b border-outline-variant/70 px-4 py-2.5 sm:px-5" aria-labelledby="customer-information-title">
              <div className="mb-2 flex items-center justify-between gap-3">
                <h3 id="customer-information-title" className="font-label-md font-semibold text-on-surface">{t('admin.sales.customer.title')}</h3>
                <span className="text-[11px] text-secondary">{t('admin.sales.customer.optional')}</span>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 2xl:grid-cols-[1fr_0.8fr_1.2fr_1fr]">
                <label className="font-label-sm text-on-surface">
                  <span className="sr-only">{t('admin.sales.customer.name')}</span>
                  <input aria-label={t('admin.sales.customer.name')} value={customerName} maxLength={200} onChange={(event) => { setCustomerName(event.target.value); setSubmitError(''); }} className={inputClass} placeholder={t('admin.sales.customer.name')} />
                </label>
                <label className="font-label-sm text-on-surface">
                  <span className="sr-only">{t('admin.sales.customer.phone')}</span>
                  <input aria-label={t('admin.sales.customer.phone')} value={customerPhone} maxLength={30} onChange={(event) => { setCustomerPhone(event.target.value); setSubmitError(''); }} className={inputClass} placeholder={t('admin.sales.customer.phone')} inputMode="tel" />
                </label>
                <label className="font-label-sm text-on-surface sm:col-span-2 2xl:col-span-1">
                  <span className="sr-only">{t('admin.sales.customer.address')}</span>
                  <input aria-label={t('admin.sales.customer.address')} value={customerAddress} maxLength={500} onChange={(event) => { setCustomerAddress(event.target.value); setSubmitError(''); }} className={inputClass} placeholder={t('admin.sales.customer.address')} />
                </label>
                <label className="font-label-sm text-on-surface sm:col-span-2 2xl:col-span-1">
                  <span className="sr-only">{t('admin.sales.customer.note')}</span>
                  <input aria-label={t('admin.sales.customer.note')} value={note} maxLength={2000} onChange={(event) => { setNote(event.target.value); setSubmitError(''); }} className={inputClass} placeholder={t('admin.sales.customer.note_placeholder')} />
                </label>
              </div>
            </section>

            <section className="px-4 py-3 sm:px-5" aria-labelledby="checkout-title">
              <h3 id="checkout-title" className="sr-only">{t('admin.sales.checkout.title')}</h3>
              <div className="grid gap-3 2xl:grid-cols-[minmax(210px,0.8fr)_minmax(0,1.2fr)]">
                <div className="space-y-1.5">
                  <div className="flex justify-between text-body-sm text-secondary"><span>{t('admin.sales.checkout.subtotal')}</span><span className="font-mono text-on-surface">{formatCurrency(subtotal)}</span></div>
                  <div className="flex justify-between text-body-sm text-secondary"><span>{t('admin.sales.checkout.discount')}</span><span className="font-mono text-on-surface">{formatCurrency(0)}</span></div>
                  <div className="flex items-end justify-between border-t border-outline-variant pt-1.5"><span className="font-label-md font-semibold text-on-surface">{t('admin.sales.checkout.total')}</span><span className="font-mono text-lg font-bold text-primary">{formatCurrency(subtotal)}</span></div>
                </div>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <label className="font-label-sm text-on-surface">
                    <span className="mb-1 block text-[11px] text-secondary">{t('admin.sales.checkout.payment_method')}</span>
                    <select className={inputClass} value="CASH" disabled aria-label={t('admin.sales.checkout.payment_method')}>
                      <option value="CASH">{t('admin.sales.checkout.cash')}</option>
                    </select>
                  </label>
                  <label className="font-label-sm text-on-surface">
                    <span className="mb-1 block text-[11px] text-secondary">{t('admin.sales.checkout.amount_received')} <span className="text-error">*</span></span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      value={paidAmountInput}
                      onChange={(event) => { setPaidAmountInput(event.target.value); setSubmitError(''); }}
                      className={`${inputClass} font-mono`}
                      placeholder="0"
                      aria-label={t('admin.sales.checkout.amount_received')}
                    />
                  </label>
                  <div className="flex items-center justify-between rounded-xl bg-surface-container-low px-3 py-2 text-body-sm text-secondary sm:col-span-2">
                    <span>{t('admin.sales.checkout.change')}</span>
                    <strong className="font-mono text-primary">{formatCurrency(changeAmount)}</strong>
                  </div>
                </div>
              </div>

              {submitError && <p className="mt-2 rounded-xl border border-error/30 bg-error-container/40 px-3 py-2 text-body-sm text-error" role="alert">{submitError}</p>}
              <button
                type="button"
                disabled={cart.length === 0 || isSubmitting}
                onClick={() => void handleSubmitSale()}
                className="mt-3 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 font-label-md font-semibold text-on-primary shadow-sm transition-colors hover:bg-on-primary-fixed-variant focus:outline-none focus:ring-2 focus:ring-primary/25 disabled:cursor-not-allowed disabled:bg-primary/45 disabled:text-on-primary/85"
              >
                <span className={`material-symbols-outlined text-[20px] ${isSubmitting ? 'animate-spin' : ''}`}>{isSubmitting ? 'progress_activity' : 'payments'}</span>
                {isSubmitting
                  ? t('admin.sales.checkout.paying')
                  : t('admin.sales.checkout.pay', { amount: formatCurrency(subtotal) })}
              </button>
            </section>
          </div>
        </section>

        <section className="flex min-h-[580px] min-w-0 flex-col overflow-hidden rounded-[20px] border border-outline-variant/70 bg-white shadow-sm xl:min-h-0">
          <div className="flex shrink-0 flex-col gap-3 border-b border-outline-variant px-4 py-3.5 sm:px-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-h2 text-h2 text-on-surface">{t('admin.sales.products.title')}</h2>
                <p className="mt-0.5 text-body-sm text-secondary">{t('admin.sales.products.variants', { shown: filteredProducts.length, total: products.length })}</p>
              </div>
              <span className="rounded-full bg-surface-container-low p-2 material-symbols-outlined text-[20px] text-primary">inventory_2</span>
            </div>
            <div className="relative w-full">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-[20px] text-secondary">search</span>
              <input value={search} onChange={(event) => setSearch(event.target.value)} className="h-11 w-full rounded-xl border border-outline-variant bg-white pl-10 pr-3 font-body-sm text-body-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" placeholder={t('admin.sales.products.search_placeholder')} aria-label={t('admin.sales.products.search_label')} />
            </div>
          </div>
          <div className="grid min-h-0 flex-1 grid-cols-1 content-start gap-2 overscroll-contain overflow-y-auto p-3 sm:grid-cols-2 2xl:grid-cols-3">
            {isLoadingProducts && <div className="col-span-full py-12 text-center text-secondary">{t('admin.sales.products.loading')}</div>}
            {!isLoadingProducts && productsError && (
              <div className="col-span-full flex flex-col items-center gap-2 py-12 text-center text-error" role="alert">
                <span>{t(productsError)}</span>
                <button type="button" className="font-label-sm text-primary hover:underline" onClick={() => void loadProducts()}>{t('admin.sales_history.actions.retry')}</button>
              </div>
            )}
            {!productsError && filteredProducts.map((product) => (
              <PosProductCard
                key={product.id}
                product={product}
                isLoading={loadingVariantId === product.id}
                onSelect={(selectedProduct) => void handleProductClick(selectedProduct)}
              />
            ))}
            {!isLoadingProducts && !productsError && filteredProducts.length === 0 && <div className="col-span-full py-12 text-center text-secondary">{t('admin.sales.products.empty')}</div>}
          </div>
        </section>
      </div>

      {productItemSelection && (
        <ProductItemSelectorModal
          productLabel={`${productItemSelection.product.name} - ${productItemSelection.product.variant}`}
          items={productItemSelection.items}
          selectedItemIds={productItemSelection.selectedItemIds}
          blockedItemIds={blockedProductItemIds}
          maxSelection={Math.min(productItemSelection.product.stock, productItemSelection.items.length)}
          onClose={() => setProductItemSelection(null)}
          onConfirm={confirmProductItems}
        />
      )}
    </div>
  );
};

export default AdminSalesPage;
